import React, { useMemo } from 'react';
import { BaseComponentImpl } from '../../../types/implementations';
import { BaseComponent, Building, PhotoModel } from '../../../types/protocols';
import BlueprintWithMarkers from '../../../Components/BlueprintWithMarkers';
import LocationDisplay, { useComponentLocations } from '../../../Components/LocationDisplay';
import {
    PhotoLocationSide,
    componentTypeCountNames,
    photoLocationList,
    photoLocationSummaryText,
} from '../../../Helpers/photoLocations';
import { photoMarkerComponent, photoModelFromLocation } from '../../../Helpers/photoMarkerGroups';
import { BaseType } from '../../../types/Enums';
import { translate } from '../../../types/EnumTranslations';
import ListView from './ListView';

interface PhotoViewProps {
    components: BaseComponentImpl[];
    building: Building;
    buildingKey: string;
    selectedKey: string | null;
    hoveredKey: string | null;
    onComponentClick: (key: string) => void;
    onComponentHover: (key: string | null) => void;
    /** Type the counts are worded for ("12 fönster i 5 foton"); defaults to the first component's type. */
    componentType?: BaseType;
}

/** One photo of a parent component, with the components pinned on it. */
interface PhotoGroup {
    storagePath: string;
    photoModel: PhotoModel;
    /** Components wrapped so BlueprintWithMarkers reads their pin via blueprintLocations[storagePath]. */
    markerComponents: BaseComponentImpl[];
}

/** A section per parent component; all of the parent's photos share it. Outer (facade/roof) sections come first. */
interface ParentSection {
    key: string;
    parentKey: string | undefined;
    side: PhotoLocationSide;
    photos: PhotoGroup[];
}

/**
 * Small satellite thumbnail of the parent component's position on the current map image
 * (same thumbnail as "Samtliga bilder"; click opens it full screen). Renders nothing when
 * the parent is not placed on the current map image.
 */
const ParentMapThumbnail: React.FC<{ parent: BaseComponent; building: Building }> = ({ parent, building }) => {
    const locations = useComponentLocations(parent, building);
    const currentMapImageKey = building.model.currentMapImageKey;
    const currentMapPath = currentMapImageKey ? building.model.mapImages?.[currentMapImageKey]?.storagePath : undefined;

    const mapLocations = useMemo(
        () => locations.filter(l => l.type === 'map' && l.storagePath === currentMapPath),
        [locations, currentMapPath]
    );

    if (mapLocations.length === 0) return null;
    return <LocationDisplay locations={mapLocations} />;
};

const ParentSectionHeader: React.FC<{ section: ParentSection; building: Building }> = ({ section, building }) => {
    const parentData = section.parentKey ? building.model.structure?.components?.[section.parentKey] : undefined;
    // displayName already includes the cardinal direction in parentheses
    const title = parentData ? new BaseComponentImpl(parentData).displayName(building) : 'Foto';

    return (
        <div className="generic-component-photo-section-header">
            <h4>{title}</h4>
            {parentData && <ParentMapThumbnail parent={parentData} building={building} />}
        </div>
    );
};

const PhotoView: React.FC<PhotoViewProps> = ({
    components,
    building,
    buildingKey,
    selectedKey,
    hoveredKey,
    onComponentClick,
    onComponentHover,
    componentType,
}) => {
    const { sections, unlocatedComponents, summary } = useMemo(() => {
        const type = componentType ?? components[0]?.type;
        const names = componentTypeCountNames(type, type ? translate(type) : 'komponent');
        const pinCountByKey = new Map<string, number>();

        // section key -> photo storagePath -> photo group, both in first-seen order
        const sectionMap = new Map<string, { parentKey: string | undefined; side: PhotoLocationSide; photos: Map<string, PhotoGroup> }>();
        const unlocated: BaseComponentImpl[] = [];

        components.forEach(component => {
            // A component marked in several photos appears under each of them.
            const pins = photoLocationList(component, building);
            if (pins.length === 0) {
                unlocated.push(component);
                return;
            }
            pinCountByKey.set(component.key, pins.length);

            pins.forEach(({ location: loc, side }) => {
                // Photos of the same parent share one section. A photo without a known parent
                // can't be tied to any other photo, so it keeps a section of its own.
                const sectionKey = loc.parentKey || loc.storagePath;
                let section = sectionMap.get(sectionKey);
                if (!section) {
                    section = { parentKey: loc.parentKey || undefined, side, photos: new Map() };
                    sectionMap.set(sectionKey, section);
                }

                let photo = section.photos.get(loc.storagePath);
                if (!photo) {
                    photo = { storagePath: loc.storagePath, photoModel: photoModelFromLocation(loc), markerComponents: [] };
                    section.photos.set(loc.storagePath, photo);
                }
                photo.markerComponents.push(photoMarkerComponent(component, loc));
            });
        });

        // Outer sections (facade, roof) first, otherwise first-seen order.
        const sections: ParentSection[] = Array.from(sectionMap.entries())
            .map(([key, section]) => ({
                key,
                parentKey: section.parentKey,
                side: section.side,
                photos: Array.from(section.photos.values()),
            }))
            .sort((a, b) => (a.side === b.side ? 0 : a.side === 'outer' ? -1 : 1));

        const photoCount = sections.reduce((sum, section) => sum + section.photos.length, 0);
        const summary = photoLocationSummaryText(pinCountByKey.size, photoCount, names.singular, names.plural);

        return { sections, unlocatedComponents: unlocated, summary };
    }, [components, building, componentType]);

    if (sections.length === 0 && unlocatedComponents.length === 0) {
        return <p>Inga foton tillgängliga</p>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {sections.length > 0 && (
                <p className="generic-component-photo-summary">{summary}</p>
            )}
            {sections.map(section => (
                <div key={section.key}>
                    <ParentSectionHeader section={section} building={building} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {section.photos.map(photo => (
                            <div key={photo.storagePath}>
                                <BlueprintWithMarkers
                                    blueprint={photo.photoModel}
                                    currentBlueprintKey={photo.storagePath}
                                    building={building}
                                    utilities={photo.markerComponents}
                                    selectedUtilityKey={selectedKey}
                                    onUtilityClick={onComponentClick}
                                    hideTitle
                                    compactMarkers
                                />
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {unlocatedComponents.length > 0 && (
                <div>
                    <h4 className="generic-component-section-header">
                        Saknar positionsfoto:
                    </h4>
                    <ListView
                        components={unlocatedComponents}
                        building={building}
                        buildingKey={buildingKey}
                        selectedKey={selectedKey}
                        onComponentClick={onComponentClick}
                        groupingStrategy="none"
                    />
                </div>
            )}
        </div>
    );
};

export default PhotoView;
